export interface FormFieldValidation {
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  custom?: string;
}

export interface FormFieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FormFieldConditional {
  fieldId: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than';
  value: string;
}

export interface FormField {
  id: string;
  type: 'text' | 'textarea' | 'email' | 'phone' | 'number' | 'currency' | 'date' | 'datetime' | 
        'select' | 'multiselect' | 'radio' | 'checkbox' | 'file' | 'signature' | 'section' | 'divider';
  label: string;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  validation?: FormFieldValidation;
  options?: FormFieldOption[];
  conditional?: FormFieldConditional;
  order: number;
  width?: 'full' | 'half' | 'third' | 'quarter';
}

export interface FormStyling {
  theme?: 'default' | 'compact' | 'spacious';
  primaryColor?: string;
  layout?: 'single-column' | 'two-column' | 'wizard';
}

export interface FormNotification {
  type: 'email' | 'webhook' | 'internal';
  recipient: string;
  template?: string;
}

export interface FormNotifications {
  onSubmit?: FormNotification[];
}

export interface FormDefinition {
  id: string;
  name: string;
  description?: string;
  version: string;
  isActive: boolean;
  category: 'Loan Application' | 'Client Onboarding' | 'Document Collection' | 'Risk Assessment' | 'Other';
  fields: FormField[];
  styling?: FormStyling;
  notifications?: FormNotifications;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface FormSubmission {
  id: string;
  formId: string;
  formVersion: string;
  submissionData: Record<string, unknown>;
  submittedBy?: string;
  submittedAt: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  validationErrors?: Record<string, string[]>;
  rulesExecuted?: string[];
  metadata?: Record<string, unknown>;
}

export interface CreateFormRequest {
  name: string;
  description?: string;
  category: FormDefinition['category'];
  fields: FormField[];
  styling?: FormStyling;
  notifications?: FormNotifications;
  isActive?: boolean;
}

export type UpdateFormRequest = Partial<CreateFormRequest>;

export interface SubmitFormRequest {
  formId: string;
  submissionData: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface FormFilters {
  category?: string;
  isActive?: boolean;
  version?: string;
}

export const FORM_CATEGORIES = [
  'Loan Application',
  'Client Onboarding', 
  'Document Collection',
  'Risk Assessment',
  'Other'
] as const;

export const FORM_FIELD_TYPES = [
  'text',
  'textarea', 
  'email',
  'phone',
  'number',
  'currency',
  'date',
  'datetime',
  'select',
  'multiselect',
  'radio',
  'checkbox',
  'file',
  'signature',
  'section',
  'divider'
] as const;

export const CONDITIONAL_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'greater_than',
  'less_than'
] as const;

export const FIELD_WIDTHS = [
  'full',
  'half', 
  'third',
  'quarter'
] as const;

export type FormCategory = typeof FORM_CATEGORIES[number];
export type FormFieldType = typeof FORM_FIELD_TYPES[number];
export type ConditionalOperator = typeof CONDITIONAL_OPERATORS[number];
export type FieldWidth = typeof FIELD_WIDTHS[number];